import React, { useState, useCallback, useEffect } from "react";
import "./ServiceTool.scss";
import "../../layouts/MainLayout.scss";
import Pagination from "../../../src/utils/components/common/pagination";
import Icons from "../../utils/components/icons";
import LoadingSpinner from "../../utils/components/common/loadingSpinner/LoadingSpinner";
import { useTranslation } from "react-i18next";
import { getServiceToolLogs } from "../../services/ServiceToolService";
import EmptyScreen from "../../utils/components/common/EmptyScreen/EmptyScreen";
import { formatDate } from "../../utils/utils";

const Logs = ({ selectedDevice, appLoading }) => {
  const { t } = useTranslation("serviceTool");
  const [currentPage, setCurrentPage] = useState(1);
  const [logList, setLogList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPath, setCurrentPath] = useState([]);
  const recordsPerPage = 10;
  const [responseStatusCode, setresponseStatusCode] = useState([]);

  const getLogsList = useCallback(
    async (currentPageNo, path = [], directory) => {
      setIsLoading(true);

      try {
        const queryParams = {
          deviceId: selectedDevice[0] ? selectedDevice[0] : "",
          size: recordsPerPage,
          page: currentPageNo,
          ...(directory && { folderName: directory }),
        };

        const response = await getServiceToolLogs(queryParams);
        if (response.data.statusCode === "SUCCESS") {
          setLogList(response.data.logInfo);
          setTotalCount(response.data.totalCount);
        } else {
          throw new Error(response.message || "Failed to fetch logs");
        }
      } catch (error) {
        console.log("Error fetching logs:", error);
        setLogList([]);
        setTotalCount(0);
        if (error.response.data.message === "NOT_FOUND") {
          setresponseStatusCode("notFound");
        } else if (error.response.data.message === "SERVER_ERROR") {
          setresponseStatusCode("serverError");
        } else {
          setresponseStatusCode("error");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [selectedDevice]
  );

  const handleRefresh = useCallback(() => {
    setCurrentPath([]);
    setCurrentPage(1);
    getLogsList(1, []);
  }, [getLogsList]);

  useEffect(() => {
    if (selectedDevice && selectedDevice.length > 0 && !appLoading) {
      handleRefresh();
    }
  }, [selectedDevice, handleRefresh, appLoading]);

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page + 1);
      getLogsList(page + 1, currentPath, currentPath[currentPath.length - 1]);
    },
    [getLogsList, currentPath]
  );

  const handleFolderClick = useCallback(
    (folderName) => {
      setCurrentPath((prevPath) => {
        if (prevPath[prevPath.length - 1] !== folderName) {
          return [...prevPath, folderName];
        }
        return prevPath;
      });
      setCurrentPage(1);
      getLogsList(1, [], folderName);
    },
    [getLogsList]
  );
  window.handleFolderClick = handleFolderClick;

  const formatFileSize = (size) => {
    if (size === null || size === undefined || size === 0 || size === "-") {
      return "-";
    }
    const fileSizeInMB = size / (1024 * 1024);
    return `${fileSizeInMB.toFixed(2)} MB`;
  };

  return (
    <>
      {isLoading ? (
        <div className="log-table-container empty-screen">
          <div className="table-header heading-h1">
            <div>
              <div className="breadcrumb heading-h1">
                {currentPath.length > 0 ? (
                  <span
                    className="breadcrumb-item heading-h1 clickable"
                    onClick={() => {
                      setCurrentPath([]);
                      setCurrentPage(1);
                      getLogsList(1, []);
                    }}
                  >
                    {t("Logs.main_header")}
                  </span>
                ) : (
                  <span className="breadcrumb-item heading-h1">
                    {t("Logs.main_header")}
                  </span>
                )}
                {currentPath.map((folder, index) => (
                  <React.Fragment key={index}>
                    <span className="breadcrumb-separator heading-h1">/</span>
                    {index === currentPath.length - 1 ? (
                      <span className="breadcrumb-item heading-h1">
                        {folder}
                      </span>
                    ) : (
                      <span
                        className="breadcrumb-item heading-h1 clickable"
                        onClick={() => {
                          setCurrentPath(currentPath.slice(0, index + 1));
                          setCurrentPage(1);
                          getLogsList(
                            1,
                            [],
                            currentPath.slice(0, index + 1).join("/")
                          );
                        }}
                      >
                        {folder}
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <button className="refresh-button" onClick={handleRefresh}>
              {Icons.refreshIcon}
            </button>
          </div>
          <div className="loading-container">
            <LoadingSpinner />
            <p className="loading-message heading-h1">{t("Logs.loaderText")}</p>
          </div>
        </div>
      ) : logList.length === 0 || responseStatusCode === "serverError" ? (
        <div className="log-table-container empty-screen">
          <div className="table-header heading-h1">
            <div>
              <div className="breadcrumb heading-h1">
                {currentPath.length > 0 ? (
                  <span
                    className="breadcrumb-item heading-h1 clickable"
                    onClick={() => {
                      setCurrentPath([]);
                      setCurrentPage(1);
                      getLogsList(1, []);
                    }}
                  >
                    {t("Logs.main_header")}
                  </span>
                ) : (
                  <span className="breadcrumb-item heading-h1">
                    {t("Logs.main_header")}
                  </span>
                )}
                {currentPath.map((folder, index) => (
                  <React.Fragment key={index}>
                    <span className="breadcrumb-separator heading-h1">/</span>
                    {index === currentPath.length - 1 ? (
                      <span className="breadcrumb-item heading-h1">
                        {folder}
                      </span>
                    ) : (
                      <span
                        className="breadcrumb-item heading-h1 clickable"
                        onClick={() => {
                          setCurrentPath(currentPath.slice(0, index + 1));
                          setCurrentPage(1);
                          getLogsList(
                            1,
                            [],
                            currentPath.slice(0, index + 1).join("/")
                          );
                        }}
                      >
                        {folder}
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <button className="refresh-button" onClick={handleRefresh}>
              {Icons.refreshIcon}
            </button>
          </div>
          <div className="empty-screen-box">
            <EmptyScreen
              title={t("Logs.fetchLog")}
              img={Icons.sdCardIcon}
              desc={t("Logs.fetchLogDesc")}
            />
          </div>
        </div>
      ) : responseStatusCode === "notFound" ? (
        <div className="log-table-container empty-screen">
          <div className="table-header heading-h1">
            <div>
              <div className="breadcrumb heading-h1">
                {currentPath.length > 0 ? (
                  <span
                    className="breadcrumb-item heading-h1 clickable"
                    onClick={() => {
                      setCurrentPath([]);
                      setCurrentPage(1);
                      getLogsList(1, []);
                    }}
                  >
                    {t("Logs.main_header")}
                  </span>
                ) : (
                  <span className="breadcrumb-item heading-h1">
                    {t("Logs.main_header")}
                  </span>
                )}
                {currentPath.map((folder, index) => (
                  <React.Fragment key={index}>
                    <span className="breadcrumb-separator heading-h1">/</span>
                    {index === currentPath.length - 1 ? (
                      <span className="breadcrumb-item heading-h1">
                        {folder}
                      </span>
                    ) : (
                      <span
                        className="breadcrumb-item heading-h1 clickable"
                        onClick={() => {
                          setCurrentPath(currentPath.slice(0, index + 1));
                          setCurrentPage(1);
                          getLogsList(
                            1,
                            [],
                            currentPath.slice(0, index + 1).join("/")
                          );
                        }}
                      >
                        {folder}
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <button className="refresh-button" onClick={handleRefresh}>
              {Icons.refreshIcon}
            </button>
          </div>
          <div className="empty-screen-box">
            <EmptyScreen
              title={t("Logs.noLog")}
              img={Icons.sdCardIcon}
              desc={t("Logs.noLogDesc")}
            />
          </div>
        </div>
      ) : (
        <div className="log-table-container">
          <div className="table-header heading-h1">
            <div>
              <div className="breadcrumb heading-h1">
                {currentPath.length > 0 ? (
                  <span
                    className="breadcrumb-item heading-h1 clickable"
                    onClick={() => {
                      setCurrentPath([]);
                      setCurrentPage(1);
                      getLogsList(1, []);
                    }}
                  >
                    {t("Logs.main_header")}
                  </span>
                ) : (
                  <span className="breadcrumb-item heading-h1">
                    {t("Logs.main_header")}
                  </span>
                )}
                {currentPath.map((folder, index) => (
                  <React.Fragment key={index}>
                    <span className="breadcrumb-separator heading-h1">/</span>
                    {index === currentPath.length - 1 ? (
                      <span className="breadcrumb-item heading-h1">
                        {folder}
                      </span>
                    ) : (
                      <span
                        className="breadcrumb-item heading-h1 clickable"
                        onClick={() => {
                          setCurrentPath(currentPath.slice(0, index + 1));
                          setCurrentPage(1);
                          getLogsList(
                            1,
                            [],
                            currentPath.slice(0, index + 1).join("/")
                          );
                        }}
                      >
                        {folder}
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <button className="refresh-button" onClick={handleRefresh}>
              {Icons.refreshIcon}
            </button>
          </div>
          <table className="log-table table table-striped theme-table">
            <thead>
              <tr>
                <th className="text-h3">{t("Logs.name")}</th>
                <th className="text-h3">{t("Logs.type")}</th>
                <th className="text-h3">{t("Logs.lastUpdated")}</th>
                <th className="text-h3">{t("Logs.fileSize")}</th>
              </tr>
            </thead>
            <tbody>
              {logList.map((item, index) => (
                <tr key={index}>
                  <td className="text-h2">
                    <div
                      className="name-container"
                      onClick={() =>
                        item.type === "File Folder" &&
                        handleFolderClick(item.name)
                      }
                    >
                      {item.type === "File Folder" && Icons.folderIcon}
                      <span
                        className={`folder-link text-h2 ${
                          item.type === "File Folder"
                            ? "file-folder"
                            : "file-other"
                        }`}
                        title={item.name}
                      >
                        {item.name || "-"}
                      </span>
                    </div>
                  </td>
                  <td
                    className={`folder-type text-h2 ${
                      item.type === "File Folder" ? "file-folder" : "file-other"
                    }`}
                  >
                    {item.type || "-"}
                  </td>
                  <td
                    className={`last-updated text-h2 ${
                      item.type === "File Folder" ? "file-folder" : "file-other"
                    }`}
                  >
                    {item.updatedAt ? formatDate(item.updatedAt) : "-"}
                  </td>
                  <td
                    className={`file-size text-h2 ${
                      item.type === "File Folder" ? "file-folder" : "file-other"
                    }`}
                  >
                    {formatFileSize(item.size)}
                  </td>
                  <td
                    className={`download text-h2 ${item.type
                      .toLowerCase()
                      .replace(" ", "-")}`}
                  >
                    {item.type !== "File Folder" ? t("Logs.download") : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="bodyFooter footerFixed">
            {
              <Pagination
                currentPage={currentPage - 1}
                totalPages={Math.ceil(totalCount / recordsPerPage)}
                pageCallBack={handlePageChange}
              />
            }
          </div>
        </div>
      )}
    </>
  );
};

export default Logs;
